import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, Eye, EyeOff } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const EMPTY = {
  trade_name: "", city: "", state: "", regions: [], service_types: [], specialties: "",
  phone: "", whatsapp: "", site: "", hours: "", description: "", active: false,
};

export default function DirectoryProfile() {
  const [form, setForm] = useState(EMPTY);
  const [rawLists, setRawLists] = useState({ regions: "", service_types: "" });
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exists, setExists] = useState(false);

  const load = () => {
    api.get("/directory/profile").then((r) => {
      if (r.data && r.data.id) {
        setForm({ ...EMPTY, ...r.data });
        setRawLists({
          regions: (r.data.regions || []).join(", "),
          service_types: (r.data.service_types || []).join(", "),
        });
        setExists(true);
      }
    }).catch(() => {});
    api.get("/directory/leads").then((r) => setLeads(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const parseList = (text) => text.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);

  const save = async (active) => {
    setSaving(true);
    try {
      await api.put("/directory/profile", { ...form, regions: parseList(rawLists.regions), service_types: parseList(rawLists.service_types), active });
      toast.success(active ? "Perfil público ativado!" : "Perfil salvo.");
      setForm((f) => ({ ...f, active }));
      setExists(true);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 animate-in-up max-w-2xl" data-testid="directory-profile-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Store className="h-6 w-6 text-sky-600" /> Meu perfil no diretório</h1>
        <p className="text-sm text-slate-500">Opcional. Você controla a visibilidade e os dados exibidos.</p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            Visibilidade pública
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{form.active ? "Visível" : "Oculto"}</span>
              <Switch checked={form.active} onCheckedChange={(v) => save(v)} data-testid="dir-visibility-toggle" />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5"><Label>Nome comercial *</Label><Input className="h-12" data-testid="dp-name" value={form.trade_name} onChange={set("trade_name")} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Cidade</Label><Input className="h-12" data-testid="dp-city" value={form.city} onChange={set("city")} /></div>
            <div className="space-y-1.5"><Label>UF</Label><Input className="h-12" maxLength={2} data-testid="dp-state" value={form.state} onChange={set("state")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Regiões atendidas (separadas por vírgula)</Label><Input className="h-12" data-testid="dp-regions" value={rawLists.regions} onChange={(e) => setRawLists({ ...rawLists, regions: e.target.value })} placeholder="Centro, Zona Norte, Zona Sul" /></div>
          <div className="space-y-1.5"><Label>Tipos de serviço (separados por vírgula)</Label><Input className="h-12" data-testid="dp-services" value={rawLists.service_types} onChange={(e) => setRawLists({ ...rawLists, service_types: e.target.value })} placeholder="Instalação, Manutenção, Câmaras frias" /></div>
          <div className="space-y-1.5"><Label>Especialidades</Label><Input className="h-12" data-testid="dp-specialties" value={form.specialties} onChange={set("specialties")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Telefone</Label><Input className="h-12" data-testid="dp-phone" value={form.phone} onChange={set("phone")} /></div>
            <div className="space-y-1.5"><Label>WhatsApp</Label><Input className="h-12" data-testid="dp-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Site ou rede social</Label><Input className="h-12" data-testid="dp-site" value={form.site} onChange={set("site")} /></div>
          <div className="space-y-1.5"><Label>Horário de atendimento</Label><Input className="h-12" data-testid="dp-hours" value={form.hours} onChange={set("hours")} placeholder="Seg a Sex, 8h às 18h" /></div>
          <div className="space-y-1.5"><Label>Descrição profissional</Label><Textarea rows={3} data-testid="dp-description" value={form.description} onChange={set("description")} /></div>
          <p className="text-xs text-slate-500">
            Não exibimos endereço residencial nem dados sensíveis. O GEPR facilita o contato; orçamento, preço e
            garantia são entre você e o cliente.
          </p>
          <div className="flex gap-2">
            <Button className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => save(form.active)} disabled={saving || !form.trade_name} data-testid="dp-save-btn">
              {saving ? "Salvando..." : exists ? "Salvar alterações" : "Criar perfil"}
            </Button>
            <Button variant="outline" className="h-12" onClick={() => save(!form.active)} disabled={saving || !form.trade_name} data-testid="dp-toggle-btn">
              {form.active ? <><EyeOff className="h-4 w-4 mr-1" /> Desativar</> : <><Eye className="h-4 w-4 mr-1" /> Ativar</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Leads recebidos ({leads.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {leads.length === 0 && <p className="text-sm text-slate-500">Nenhum lead ainda. Ative seu perfil para ser encontrado.</p>}
          {leads.map((l) => (
            <div key={l.id} className="border border-slate-200 rounded-lg p-3.5" data-testid={`lead-${l.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{l.name} · {l.phone}</p>
                <Badge variant="outline" className="text-[10px]">{l.status}</Badge>
              </div>
              {l.message && <p className="text-xs text-slate-600 mt-1">{l.message}</p>}
              <p className="text-[11px] text-slate-400 mt-1">{l.city} · {fmtDate(l.created_at)}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
